/**
 * rutasSueltas.js — las páginas públicas que NO están en `PAGINAS`.
 *
 * ── QUÉ RESUELVE ────────────────────────────────────────────────────────────
 *
 * Las cinco legales las enlaza el pie de las CINCUENTA páginas de marketing.
 * Son, con diferencia, las URLs con más enlaces internos entrantes de todo el
 * sitio. Y hasta aquí servían la cáscara del SPA: 3 KB con
 * `<title>Mytapstar</title>`, sin descripción, sin canonical y sin `lang`.
 * Google las rastrea sí o sí —tienen cincuenta enlaces apuntándolas— y lo que
 * encontraba era una página en blanco.
 *
 * ── POR QUÉ UNA TABLA APARTE Y NO DENTRO DE `PAGINAS` ───────────────────────
 *
 * Porque no son la misma clase de cosa, y meterlas ahí rompería dos invariantes
 * que sí importan:
 *
 *  1. **`PAGINAS` tiene cinco URLs por página, una por idioma.** Estas tienen
 *     UNA sola para los cinco. Metidas en la tabla, el test de
 *     `rutasPublicas.test.js` que comprueba que no se repite ninguna URL
 *     fallaría — y con razón, porque cinco claves de idioma apuntando al mismo
 *     `/terms` es exactamente el descuadre que ese test vigila.
 *  2. **Todo lo de `PAGINAS` lleva `hreflang`.** Estas no pueden: no hay cinco
 *     versiones entre las que elegir.
 *
 * ── SE PRERENDERIZAN EN ESPAÑOL ─────────────────────────────────────────────
 *
 * Una URL no puede servir cinco idiomas estáticos. El HTML sale en español —el
 * idioma de la raíz— y el cliente repinta el texto en el del visitante al
 * montar, porque el contenido vive en i18n (`translation.json`, clave `*_doc`).
 * Lo que se queda en español es el `<title>` de la pestaña. Es el precio de
 * tener una sola URL, y es la decisión que ya estaba tomada: traducir las URLs
 * legales mueve cinco páginas que no captan a nadie a cambio de nada.
 */

/** La imagen social. La misma que usan la home y los sectores. */
const OG_DEFECTO = '/landing-images/v3/og/og-default.png';

/**
 * Las páginas sueltas, por clave interna.
 *
 * `titulo` y `descripcion` van en español y escritos a mano, no derivados del
 * `title` del documento: el del documento es un encabezado («Términos de
 * Servicio») y esto es lo que se lee en una lista de resultados, donde hace
 * falta decir además de quién son.
 *
 * `indexable: false` existe para la página 404, que llega en el PR siguiente:
 * una página de error no debe entrar en el índice por mucho que se pueda pedir.
 */
export const SUELTAS = {
  terms: {
    url: '/terms',
    titulo: 'Términos de Servicio | MyTapstar',
    descripcion:
      'Condiciones de uso de MyTapstar: contratación, planes, responsabilidades '
      + 'y bajas. Speed Data Technologies, S.L.',
    imagen: OG_DEFECTO,
  },
  privacy: {
    url: '/privacy',
    titulo: 'Política de Privacidad | MyTapstar',
    descripcion:
      'Qué datos trata MyTapstar, con qué base legal, cuánto los conserva y cómo '
      + 'ejercer tus derechos de acceso, rectificación y supresión.',
    imagen: OG_DEFECTO,
  },
  dpa: {
    url: '/dpa',
    titulo: 'Acuerdo de Tratamiento de Datos (DPA) | MyTapstar',
    descripcion:
      'Acuerdo de encargado de tratamiento entre tu negocio y MyTapstar: '
      + 'finalidades, medidas de seguridad, subencargados y devolución de datos.',
    imagen: OG_DEFECTO,
  },
  legalNotice: {
    url: '/legal-notice',
    titulo: 'Aviso Legal | MyTapstar',
    descripcion:
      'Datos identificativos del titular de mytapstar.com: Speed Data '
      + 'Technologies, S.L., CIF B19479864, Valencia. Condiciones de acceso al sitio.',
    imagen: OG_DEFECTO,
  },
  cookies: {
    url: '/cookies',
    titulo: 'Política de Cookies | MyTapstar',
    descripcion:
      'Qué cookies usa MyTapstar, para qué sirve cada una, cuánto duran y cómo '
      + 'cambiar tu consentimiento en cualquier momento.',
    imagen: OG_DEFECTO,
  },

  /*
   * La página de error, y es distinta de las cinco de arriba en tres cosas.
   *
   * 1. **No tiene URL propia.** Se sirve desde `error_page 404 /404.html` en
   *    CUALQUIER url que no exista. Por eso `url` aquí es el nombre del fichero
   *    y no una ruta que alguien vaya a escribir.
   * 2. **Va a `dist/404.html` y no a `dist/404/index.html`**, que es lo que
   *    haría `destino()`. De ahí el `fichero`.
   * 3. **NO lleva canonical.** Es lo que la separa de todo lo demás de este
   *    módulo: un canonical en un 404 le estaría diciendo a Google que esa url
   *    inexistente es la versión buena de algo. Tampoco lleva descripción ni
   *    Open Graph — nadie comparte un 404 y no hay nada que resumir. Solo
   *    título y `noindex`.
   */
  noEncontrada: {
    url: '/404.html',
    fichero: '404.html',
    indexable: false,
    titulo: 'Página no encontrada | MyTapstar',
  },
};

/** Las claves, en el orden en que están escritas. */
export const CLAVES_SUELTAS = Object.keys(SUELTAS);

/** Las que Google puede indexar: las cinco legales, no la de error. */
export const CLAVES_SUELTAS_INDEXABLES = CLAVES_SUELTAS
  .filter((c) => SUELTAS[c].indexable !== false);

/** La ficha de una página suelta, o `null` si la clave no existe. */
export function sueltaDe(clave) {
  return SUELTAS[clave] ?? null;
}

/**
 * La clave de página suelta de un pathname, o `null`.
 *
 * Compara en minúsculas y sin barra final, igual que `paginaDeLaRuta`, porque
 * `/Terms/` y `/terms` son la misma página para el router aunque nginx redirija
 * la primera.
 */
export function sueltaDeLaRuta(pathname) {
  if (typeof pathname !== 'string' || !pathname.startsWith('/')) return null;
  const ruta = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  return CLAVES_SUELTAS.find((c) => SUELTAS[c].url === ruta) ?? null;
}
